import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditAccountSelectField } from '../../../EditAccountSelectField';
import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditContactSelectField } from '../../../EditContactSelectField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import type { ContactWorkInfo } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactWorkInfo;
}

const WORK_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'jobTitle', formField: 'jobTitle' },
  { apiField: 'department', formField: 'department' },
  { apiField: 'newCompanyName', formField: 'newCompanyName' },
  { apiField: 'notAtCompanyFlag', formField: 'notAtCompanyFlag' },
  { apiField: 'isAssistant', formField: 'isAssistant' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactWorkInfo,
  initial: ContactWorkInfo
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : currentVal;
      }
    }
  }

  return changes;
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        jobTitle
        department
        newCompanyName
        notAtCompanyFlag
        isAssistant
        assistantId {
          id
          name
        }
        managingPartnerId {
          id
          name
        }
      }
    }
  }
`;

export interface EditWorkInfoDialogProps {
  contactId: string;
  initialValue: ContactWorkInfo;
  isOpen: boolean;
  onChange: (data: ContactWorkInfo) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactWorkInfo;
}

export function EditWorkInfoDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditWorkInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(WORK_INFO_FIELDS, value, initialValue);

  // Handle assistantId separately since we need to track the ID
  const assistantChanged =
    value.assistant !== initialValue.assistant ||
    value.assistantId !== initialValue.assistantId;
  if (assistantChanged) {
    changes.assistantId = value.assistantId || null;
  }

  // Handle managingPartnerId separately since we need to track the ID
  const managingPartnerChanged =
    value.managingPartner !== initialValue.managingPartner ||
    value.managingPartnerId !== initialValue.managingPartnerId;
  if (managingPartnerChanged) {
    changes.managingPartnerId = value.managingPartnerId || null;
  }

  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactWorkInfo>(
    field: K,
    fieldValue: ContactWorkInfo[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save work information');
      } else {
        toast.success('Work information saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving work information:', error);
      toast.error('Failed to save work information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Company/Work Information"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Job Title"
          onChange={jobTitle => {
            updateField('jobTitle', jobTitle);
          }}
          value={value.jobTitle}
        />
        <EditFormField
          label="Department"
          onChange={department => {
            updateField('department', department);
          }}
          value={value.department}
        />
        <EditFormField
          label="New Company Name"
          onChange={newCompanyName => {
            updateField('newCompanyName', newCompanyName);
          }}
          value={value.newCompanyName}
        />
        <EditDynamicSelectField
          fieldName="not_at_company_flag"
          label="Not at Company Flag"
          model="res.partner"
          onChange={v => {
            updateField('notAtCompanyFlag', v);
          }}
          value={value.notAtCompanyFlag}
        />
        <EditContactSelectField
          excludeId={contactId}
          label="Assistant"
          onChange={(id, name) => {
            onChange({
              ...value,
              assistant: name,
              assistantId: id
            });
          }}
          value={
            value.assistantId
              ? { id: value.assistantId, name: value.assistant }
              : null
          }
        />
        <EditAccountSelectField
          label="Managing Partner"
          onChange={(id, name) => {
            onChange({
              ...value,
              managingPartner: name,
              managingPartnerId: id
            });
          }}
          value={
            value.managingPartnerId
              ? { id: value.managingPartnerId, name: value.managingPartner }
              : null
          }
        />
        <EditCheckboxField
          checked={value.isAssistant}
          label="Is Assistant"
          onCheckedChange={isAssistant => {
            updateField('isAssistant', isAssistant);
          }}
        />
      </div>
    </EditDialog>
  );
}
